/**
 * As faixas de cor da ocupação, lidas da configuração.
 *
 * Os dois cortes eram constantes do front (85 e 100) e ninguém da DRO os definiu. Agora
 * eles vêm de Configurações › Ocupação, e esta é a ÚNICA regra que decide a cor: a grade
 * do Mapa, a legenda dela, o medidor "hoje", o relatório de carga x capacidade e a Ajuda
 * leem daqui, senão a legenda passa a mentir no dia em que alguém move um corte.
 *
 * Espelha apps/server/src/modules/config-institucional/faixas-ocupacao.util.ts.
 */

import { TChipTone } from '@umodeapporg/ui/components/UI/Chips/chipTones';

import { IOcupacaoConfig } from '../interfaces/configuracao';

/** Até este percentual a semana tem folga. Vale enquanto o registro não traz o grupo. */
export const OCUPACAO_NO_LIMITE_PADRAO = 95;

/** Acima deste percentual a semana não cabe: a capacidade da célula acabou. */
export const OCUPACAO_NAO_CABE_PADRAO = 100;

/** Os mesmos limites que o servidor aceita: a tela não oferece o que o Gravar recusaria. */
export const OCUPACAO_FAIXA_MIN = 1;
export const OCUPACAO_FAIXA_MAX = 200;

/** As três leituras de carga de uma semana, mais a semana que não tem carga nenhuma. */
export type FaixaDeOcupacao = 'folga' | 'limite' | 'estouro' | 'vazio';

/** As faixas em vigor, do jeito que a tela as mostra. */
export interface IFaixasDeOcupacao {
  noLimitePct: number;
  naoCabePct: number;
}

/**
 * Os dois cortes em vigor, a partir do grupo `ocupacao` da configuração. Configuração
 * ainda carregando (ou documento gravado antes do grupo) cai nos padrões, que são os
 * mesmos números que o servidor semeia: a grade nunca pisca com uma cor e corrige depois.
 */
export function faixasDeOcupacao(
  ocupacao: IOcupacaoConfig | null | undefined,
): IFaixasDeOcupacao {
  return {
    noLimitePct: ocupacao?.inicioNoLimitePct ?? OCUPACAO_NO_LIMITE_PADRAO,
    naoCabePct: ocupacao?.inicioNaoCabePct ?? OCUPACAO_NAO_CABE_PADRAO,
  };
}

/**
 * A faixa de uma ocupação. Os limites são FECHADOS no fim de cada faixa: 95% ainda tem
 * folga e 100% está no limite, não estourada. Uma semana cheia é uma semana cheia, e a
 * capacidade da célula já desconta a produtividade, então 100% não é defeito.
 *
 * Sem capacidade registrada (`null`) a semana não tem resposta para dar, e pintá-la de
 * verde diria que há folga onde ninguém disse qual é o tamanho da célula.
 */
export function faixaDaOcupacao(
  ocupacaoPct: number | null | undefined,
  faixas: IFaixasDeOcupacao,
): FaixaDeOcupacao {
  if (ocupacaoPct === null || ocupacaoPct === undefined || !Number.isFinite(ocupacaoPct)) {
    return 'vazio';
  }
  if (ocupacaoPct <= faixas.noLimitePct) {
    return 'folga';
  }
  return ocupacaoPct <= faixas.naoCabePct ? 'limite' : 'estouro';
}

/** A mesma faixa como tom de chip, para as tabelas que leem ocupação. */
const TOM_DA_FAIXA: Readonly<Record<FaixaDeOcupacao, TChipTone>> = {
  folga: 'success',
  limite: 'warning',
  estouro: 'danger',
  vazio: 'neutral',
};

export function toneDaOcupacao(
  ocupacaoPct: number | null | undefined,
  faixas: IFaixasDeOcupacao,
): TChipTone {
  return TOM_DA_FAIXA[faixaDaOcupacao(ocupacaoPct, faixas)];
}
